import React, { useState, useEffect, FC } from 'react';
import styled from 'styled-components';

import { colors } from '../constants';

// LoadingBar
// Animates a fill bar within a track to simulate loading completion
// Upon fill completion, executes completion callback passed in props
// Fill animations occur periodically until one of the following conditions is true:
//   - props.allowCompletion=true, triggering a final fill of the remaining width of the track
//   - Up to 95% of the track is filled; the last 5% is filled upon props.allowCompletion=true

// Props:
//   completionCallback: execute when when all progress animations have completed
//   allowCompletion: immediately trigger final fill animation to conclude simulation
//   completionEstimateSeconds: estimation of time in seconds before allowCompletion turns true
//   trackWidthRem: width of track to fill

interface LoadingBarProps {
  completionCallback: Function;
  allowCompletion: boolean;
  reset?: boolean;
  completionEstimateSeconds?: number;
  trackWidthRem?: number;
}

// Props for styled component 'Fill'
interface StyledProps {
  animationLength: number;
}

const MOBILE_SCALE = 0.777; // LoadingBar extends across 77.7% of mobile screen width
const MOBILE_WIDTH = 600; // MOBILE_BREAKPOINT in pixels plus a bit of margin
const FIRST_ANIMATION_DELAY = 250; // brief delay before first fill animation
const COMPLETION_FILL_TIME = 500; // length of time of fill completion animation in milliseconds
const MAX_FILL_BEFORE_COMPLETE = 0.95; // percentage of track that will fill before completion

// #region -- Styles ------------------------------------------------------------------------------
const Container = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
Container.displayName = 'Container';

const Track = styled.div`
  position: relative;
  border-radius: 0.1875rem;
  height: 0.375rem;
  width: 27rem;
  background-color: #f2f2f2;
`;
Track.displayName = 'Track';

// Animates changes in width ("progress")
const Fill = styled.div<StyledProps>`
  position: absolute;
  height: 100%;
  width: 0;
  border-radius: 0.1875rem;
  background-color: ${colors.UI_PRIMARY_ACCENT};
  transition: 1000ms ease;
  ${(p: StyledProps) =>
    p.animationLength &&
    `
    transition: ${p.animationLength}ms ease;
  `};
`;
Fill.displayName = 'Fill';
// #endregion

// Component --------------------------------------------------------------------------------------
export const LoadingBar: FC<LoadingBarProps> = ({
  completionCallback,
  allowCompletion,
  reset,
  completionEstimateSeconds,
  trackWidthRem,
}) => {
  // Support mobile dimensions - use the smaller of the two for the total track width:
  //   - 77.7% screen width
  //   - props.trackWidthRem
  const getTrackWidth = (): number => {
    const windowWidth = window ? window.innerWidth : 0;
    const isMobile = windowWidth < MOBILE_WIDTH;

    if (isMobile) {
      const trackWidthPixels = windowWidth * MOBILE_SCALE;
      return trackWidthPixels / 16;
    }
    return trackWidthRem || 0;
  };

  // Width of entire track in rem
  const trackWidth = getTrackWidth();
  const maxTrackWidth = trackWidth * MAX_FILL_BEFORE_COMPLETE;

  // Get length of time in milliseconds for single fill animation, randomly either:
  //   - 10% length of time of completion estimate
  //   - 20% length of time of completion estimate
  const getAnimationTime = (): number => {
    // Completion will use default 10 seconds if estimation is not provided
    const completionEstimate = completionEstimateSeconds ?? 0;
    // Animation time length is 1/10 of the optionally provided completion estimate
    const tenthCompletion = completionEstimate * 0.1;
    // Randomly double the animation time
    const randomlyDouble = Math.random() < 0.5;
    // Multiply by 1000 to convert to milliseconds
    const randomTimeMultiplier = randomlyDouble ? 2000 : 1000;
    // Return 10% or 20% of the estimated completion time for length of time of single animation
    const animationTime = tenthCompletion * randomTimeMultiplier;
    return animationTime;
  };

  // Get length of track in rem for single fill animation, randomly either:
  //   - 5% length of track
  //   - 10% length of track
  const getAnimationWidth = (): number => {
    // Get a tenth of the track width in rem
    const tenthCompletion = trackWidth * 0.1;
    // Randomly double the animation width
    const randomlyDouble = Math.random() < 0.5;
    // Return either 5% or 10% of the width of the track for width of single animation
    const randomWidthMultiplier = randomlyDouble ? 0.1 : 0.05;
    const animationWidth = tenthCompletion + randomWidthMultiplier;
    return animationWidth;
  };

  // State ----------------------------------------------------------------------------------------
  const [fill, setFill] = useState(0); // width of progress fill bar - start at 0% completion
  const [time, setTime] = useState(getAnimationTime()); // animation length in milliseconds

  // Effects --------------------------------------------------------------------------------------
  // Fill a small width of the track after a brief delay when page first loads
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!allowCompletion) {
      timer = setTimeout(() => {
        const incrementedFill = getAnimationWidth();
        setFill(incrementedFill);
      }, FIRST_ANIMATION_DELAY);
    }
    return () => clearTimeout(timer);
  }, []);

  // Incrementally fill the track while allowCompletion is false
  useEffect(() => {
    let timer: NodeJS.Timeout;
    // Call completion callback if allowCompletion is updated to true
    if (allowCompletion) {
      timer = setTimeout(() => {
        completionCallback();
      }, COMPLETION_FILL_TIME);
    } else if (!allowCompletion && fill < maxTrackWidth) {
      // Randomly fill either 5% or 10% of the track with each animation
      const animationWidth = getAnimationWidth();
      const animationTime = getAnimationTime();
      // Add the existing fill with the additional fill for this animation
      const afterFill = fill + animationWidth;
      // When next animation would exceed 95% width, animate slowly up to 95%
      const animationFill = Math.min(afterFill, maxTrackWidth);
      const animationLength =
        afterFill < maxTrackWidth ? animationTime : animationTime * 5;
      setTime(animationLength);
      const animate = () => {
        timer = setTimeout(() => {
          setFill(animationFill);
          animate();
        }, animationLength);
      };
      animate();
    }
    return () => clearTimeout(timer);
  }, [fill]);

  useEffect(() => {
    if (reset) {
      setFill(0);
    }
  }, [reset]);

  // Begin final fill animation right away when completion is allowed
  useEffect(() => {
    if (allowCompletion) {
      setFill(trackWidth);
    }
  }, [allowCompletion]);

  return (
    <Container id="progress-bar-container">
      <Track
        id="progress-bar-track"
        style={{
          width: `${trackWidth}rem`,
        }}
      >
        <Fill
          id="progress-bar-fill"
          animationLength={allowCompletion ? COMPLETION_FILL_TIME : time}
          style={{
            width: `${fill}rem`,
          }}
        />
      </Track>
    </Container>
  );
};

LoadingBar.defaultProps = {
  reset: false, // optionally reset progress, e.g. for retry functionality
  completionEstimateSeconds: 10, // estimate of total time spent before props.allowCompletion=true
  trackWidthRem: 27, // width in rem
};
